/**
 * The render-v2 demo plaza (`?r=2`, apps/client/src/render3d2): the gpudemo's plaza as a zone, so the rules
 * stand on what the v2 view draws (apps/client/src/tools/gpudemo/scenery.ts). It is 80 × 80 m of flat
 * flagstones at level 0, inside a courtyard wall with a gate in its south side. It holds the demo's 15
 * buildings at the same places, as solid footprints, and the well. The player wakes in the middle.
 *
 * The map's tiles are the demo's plaza metres: the demo draws the plaza at world (x − 40, z − 40), so
 * map (i, j) is demo world (i − 40, j − 40).
 */
import type { LayoutSpec } from '../../layout';

/** The plaza's side (metres), and the courtyard wall's line in from its edge (the demo's `WALL_AT`). */
const S = 80;

export const DEMO_PLAZA_LAYOUT: LayoutSpec = {
  id: 'demoPlaza',
  zone: 'demoPlaza',
  w: S,
  h: S,
  seed: 80,
  keepClear: 0,
  // Flat ground at level 0: no sea (the coast is off the map), no ranges, no noise.
  terrain: {
    coast: [
      [0, -10],
      [S, -10],
    ],
    west: [[Infinity, -10]],
    east: [1000, 0, 1, 0],
    south: [1000, 0, 1, 0, 0, 0],
    rock: { lift: 0, rise: 0, max: 0, noise: 0, scale: 1, step: 1 },
    rows: [
      [0, 0],
      [S, 0],
    ],
    slopes: [],
    plateaus: [],
    rises: [],
    hills: [],
    ridges: [],
    noise: [10, 0],
    beach: { reach: 0, base: 0, rise: 0, sand: 0, keep: [] },
  },
  ops: [
    // The flagstones inside the wall.
    { name: 'plaza', shape: { rect: [3, S - 3, 3, S - 3] }, kind: 'court', level: 0 },
    // The courtyard wall (the demo's line at 2 m and 78 m from the edges, half a metre thick), the gate's
    // span in its south side (x 35–45) left open; the gate stands in it.
    { name: 'wall N', shape: { rect: [1, S - 1, 1, 3] }, kind: 'wall', level: 0 },
    { name: 'wall W', shape: { rect: [1, 3, 1, S - 1] }, kind: 'wall', level: 0 },
    { name: 'wall E', shape: { rect: [S - 3, S - 1, 1, S - 1] }, kind: 'wall', level: 0 },
    { name: 'wall S west', shape: { rect: [1, 35, S - 3, S - 1] }, kind: 'wall', level: 0 },
    { name: 'wall S east', shape: { rect: [45, S - 1, S - 3, S - 1] }, kind: 'wall', level: 0 },
    { name: 'gateway', shape: { rect: [35, 45, S - 2, S - 1] }, kind: 'court', level: 0 },
    // The well (the demo's prop at 40, 30).
    { name: 'well', shape: { rect: [39, 41, 29, 31] }, kind: 'wall', level: 0 },
  ],
  // The demo's buildings (tools/gpudemo/scenery.ts `STRUCTURES`), footprints [x0, x1) × [y0, y1), floors at 0.
  buildings: [
    {
      id: 'demo-inn',
      name: 'โรงเตี๊ยมลมทะเล',
      kind: 'inn',
      x0: 6,
      x1: 18,
      y0: 6,
      y1: 16,
      base: 0,
      eave: 6.4,
      rise: 3.2,
      roof: 'hip',
      door: [17, 11, 'e'],
      district: 4,
    },
    {
      name: 'บ้านผู้เฒ่าหลิว',
      kind: 'house',
      x0: 34,
      x1: 42,
      y0: 6,
      y1: 13,
      base: 0,
      eave: 3.2,
      rise: 2.4,
      roof: 'hip',
      door: [38, 12, 's'],
      district: 4,
    },
    {
      name: 'หอสังเกตการณ์',
      kind: 'tower',
      x0: 66,
      x1: 70,
      y0: 6,
      y1: 10,
      base: 0,
      eave: 9,
      rise: 2.5,
      roof: 'hip',
      district: 1,
    },
    {
      name: 'บ้านลุงไห่',
      kind: 'house',
      x0: 50,
      x1: 57,
      y0: 6,
      y1: 12,
      base: 0,
      eave: 3.4,
      rise: 2.4,
      roof: 'hip',
      door: [53, 11, 's'],
      district: 5,
    },
    {
      name: 'บ้านริมลาน',
      kind: 'house',
      x0: 5,
      x1: 12,
      y0: 30,
      y1: 36,
      base: 0,
      eave: 3.2,
      rise: 2.4,
      roof: 'hip',
      door: [11, 33, 'e'],
      district: 1,
    },
    {
      name: 'ศาลเจ้า',
      kind: 'hall',
      x0: 4,
      x1: 12,
      y0: 48,
      y1: 57,
      base: 0,
      eave: 4.6,
      rise: 3.6,
      roof: 'hip',
      door: [11, 52, 'e'],
      color: 'red',
      district: 3,
    },
    {
      name: 'บ้านชาวประมง',
      kind: 'house',
      x0: 68,
      x1: 75,
      y0: 28,
      y1: 34,
      base: 0,
      eave: 3.2,
      rise: 2.4,
      roof: 'thatch',
      door: [68, 31, 'w'],
      district: 1,
    },
    {
      name: 'ร้านค้าหยานหยู',
      kind: 'house',
      x0: 66,
      x1: 74,
      y0: 46,
      y1: 53,
      base: 0,
      eave: 3.2,
      rise: 2.4,
      roof: 'hip',
      door: [70, 46, 'n'],
      district: 5,
    },
    {
      name: 'เพิง',
      kind: 'house',
      x0: 72,
      x1: 75,
      y0: 60,
      y1: 64,
      base: 0,
      eave: 2.4,
      rise: 1.3,
      roof: 'thatch',
      district: 1,
    },
    {
      name: 'แผงลอย',
      kind: 'stall',
      x0: 22,
      x1: 25,
      y0: 64,
      y1: 66,
      base: 0,
      eave: 2.2,
      rise: 0.8,
      roof: 'awning',
      color: 'red',
      district: 5,
    },
    {
      name: 'แผงลอย',
      kind: 'stall',
      x0: 26,
      x1: 29,
      y0: 64,
      y1: 66,
      base: 0,
      eave: 2.2,
      rise: 0.8,
      roof: 'awning',
      color: 'white',
      district: 5,
    },
    {
      name: 'แผงลอย',
      kind: 'stall',
      x0: 51,
      x1: 54,
      y0: 64,
      y1: 66,
      base: 0,
      eave: 2.2,
      rise: 0.8,
      roof: 'awning',
      color: 'blue',
      district: 5,
    },
    {
      name: 'แผงลอย',
      kind: 'stall',
      x0: 55,
      x1: 58,
      y0: 64,
      y1: 66,
      base: 0,
      eave: 2.2,
      rise: 0.8,
      roof: 'awning',
      color: 'red',
      district: 5,
    },
    {
      name: 'ศาลา',
      kind: 'pavilion',
      x0: 56,
      x1: 61,
      y0: 18,
      y1: 23,
      base: 0,
      eave: 3,
      rise: 2.6,
      roof: 'pyramid',
      color: 'red',
      district: 2,
    },
    {
      name: 'ประตูใต้',
      kind: 'gate',
      x0: 35,
      x1: 45,
      y0: S - 3,
      y1: S - 2,
      base: 0,
      eave: 4.5,
      rise: 1.2,
      color: 'red',
      axis: 'x',
      passage: [38, 42],
      district: 5,
    },
  ],
  boats: [],
  posts: [],
  props: [],
  landmarks: {},
  points: {
    plaza: [40, 40],
    spawn: [40, 40],
    elder: [44, 40],
    jail: [40, 40],
    duel: [40, 40],
    arena: [40, 40],
    lair: [40, 40],
  },
  regions: {
    // The wolf camp (zones.ts `demoPlaza.spawns`): the open south-east corner, between the stalls, the shed and
    // the wall, about 35 m from the player's spawn in the middle.
    wolves: [60, 72, 67, 75],
    east: [60, 70, 36, 44],
    bandits: [14, 24, 36, 44],
    west: [14, 24, 36, 44],
    graves: [30, 50, 70, 74],
  },
  portals: [],
  reserved: [],
  // The whole plaza is safe (no PK).
  safe: [
    [0, 0],
    [S, 0],
    [S, S],
    [0, S],
  ],
};
